import React, { useState } from 'react';
import { 
  AlertTriangle, Upload, X, CheckCircle, ArrowLeft, Send
} from 'lucide-react';
import { issuesApi } from '../../services/issuesApi';
import './Issues.css';

const CATEGORIES = [
  'Engine', 'Electronics', 'Hull/Gel Coat', 'Trailer', 
  'Tower/Bimini', 'Ballast System', 'Stereo/Audio', 
  'Driving / Surf Systems', 'Warranty', 'Customer Service', 'Other'
];

const BOAT_MODELS = [
  'S220', 'S240', 'Nv213', 'Nv233', 'Nv243', 'Fe22', 'Fe23', 'Fe25', 'Ri230', 'Ri245', 'Ri265'
];

// Dynamic year range: current year + 1 back to 7 years ago
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 8 }, (_, i) => currentYear + 1 - i);

export default function IssueSubmissionForm({ onNavigate, onSelectIssue }) {
  const [loading, setLoading] = useState(false);
  
  // Navigation helper
  const navigate = (path) => {
    if (onNavigate) {
      if (path === '/issues') {
        onNavigate('issues');
      } else if (path.startsWith('/issues/')) {
        const issueId = path.replace('/issues/', '');
        if (onSelectIssue) onSelectIssue(issueId);
        onNavigate('issues-detail');
      }
    }
  };
  const [successModal, setSuccessModal] = useState(null);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    boatModel: '',
    year: '',
    description: '',
    contactName: '',
    contactEmail: '',
    phone: '',
    dealerLocation: '',
    imageURLs: []
  });

  const [imagePreviews, setImagePreviews] = useState([]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > 100) {
      newErrors.title = 'Title must be 100 characters or less';
    }
    
    if (!formData.category) {
      newErrors.category = 'Please select a category';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length < 50) {
      newErrors.description = 'Description must be at least 50 characters';
    }
    
    if (!formData.contactName.trim()) {
      newErrors.contactName = 'Name is required';
    }
    
    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Please enter a valid email address';
    }
    
    if (formData.phone && !/^[\d\s\-\(\)]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    
    if (imagePreviews.length + files.length > 5) {
      alert('Maximum 5 images allowed');
      return;
    }
    
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        alert(`${file.name} is too large. Maximum size is 5MB.`);
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
        setFormData(prev => ({
          ...prev,
          imageURLs: [...prev.imageURLs, reader.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      imageURLs: prev.imageURLs.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      const result = await issuesApi.createIssue(formData);
      setSuccessModal(result);
    } catch (error) {
      console.error('Error submitting issue:', error);
      alert(error.response?.data?.error || 'Failed to submit issue. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  return (
    <div className="issue-form-page">
      <div className="issue-form-container">
        <div className="issue-form-header">
          <h1>
            <AlertTriangle size={28} style={{ marginRight: '0.5rem' }} />
            Submit an Issue
          </h1>
          <p>Help us improve by reporting problems or concerns</p>
        </div>

        <form className="issue-form" onSubmit={handleSubmit}>
          <button 
            type="button" 
            className="btn-secondary" 
            onClick={() => navigate('/issues')}
            style={{ marginBottom: '1.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <ArrowLeft size={18} />
            Back to Issues
          </button>

          {/* Title */}
          <div className="form-group">
            <label className="form-label">
              Issue Title <span className="required">*</span>
            </label>
            <input
              type="text"
              name="title"
              className={`form-input ${errors.title ? 'error' : ''}`}
              placeholder="Brief summary of the issue"
              value={formData.title}
              onChange={handleChange}
              maxLength={100}
            />
            <div className={`char-counter ${formData.title.length > 90 ? 'warning' : ''} ${formData.title.length >= 100 ? 'error' : ''}`}>
              {formData.title.length}/100
            </div>
            {errors.title && <span className="error-message">{errors.title}</span>}
          </div>

          {/* Category and Model Row */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Category <span className="required">*</span>
              </label>
              <select
                name="category"
                className={`form-select ${errors.category ? 'error' : ''}`}
                value={formData.category}
                onChange={handleChange}
              >
                <option value="">Select a category</option>
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              {errors.category && <span className="error-message">{errors.category}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Boat Model</label>
              <select
                name="boatModel"
                className="form-select"
                value={formData.boatModel}
                onChange={handleChange}
              >
                <option value="">Select model (optional)</option>
                {BOAT_MODELS.map(model => (
                  <option key={model} value={model}>{model}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Year */}
          <div className="form-group" style={{ maxWidth: '200px' }}>
            <label className="form-label">Year</label>
            <select
              name="year"
              className="form-select"
              value={formData.year}
              onChange={handleChange}
            >
              <option value="">Select year</option>
              {YEARS.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div className="form-group">
            <label className="form-label">
              Description <span className="required">*</span>
            </label>
            <textarea
              name="description"
              className={`form-textarea ${errors.description ? 'error' : ''}`}
              placeholder="Please describe the issue in detail. Include what happened, when it occurred, and any steps to reproduce the problem."
              value={formData.description}
              onChange={handleChange}
              rows={6}
            />
            <div className={`char-counter ${formData.description.length < 50 ? 'warning' : ''}`}>
              {formData.description.length} characters (minimum 50)
            </div>
            {errors.description && <span className="error-message">{errors.description}</span>}
          </div>

          {/* Contact Info */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Your Name <span className="required">*</span>
              </label>
              <input
                type="text"
                name="contactName"
                className={`form-input ${errors.contactName ? 'error' : ''}`}
                placeholder="Full name"
                value={formData.contactName}
                onChange={handleChange}
              />
              {errors.contactName && <span className="error-message">{errors.contactName}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">
                Email <span className="required">*</span>
              </label>
              <input
                type="email"
                name="contactEmail"
                className={`form-input ${errors.contactEmail ? 'error' : ''}`}
                placeholder="your@email.com"
                value={formData.contactEmail}
                onChange={handleChange}
              />
              {errors.contactEmail && <span className="error-message">{errors.contactEmail}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input
                type="tel"
                name="phone"
                className={`form-input ${errors.phone ? 'error' : ''}`}
                placeholder="(555) 555-5555"
                value={formData.phone}
                onChange={(e) => {
                  const formatted = formatPhone(e.target.value);
                  setFormData(prev => ({ ...prev, phone: formatted }));
                }}
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Dealer Location</label>
              <input
                type="text"
                name="dealerLocation"
                className="form-input"
                placeholder="City, State or dealer name"
                value={formData.dealerLocation}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Image Upload */}
          <div className="form-group">
            <label className="form-label">Upload Images</label>
            <div 
              className="image-upload-area"
              onClick={() => document.getElementById('image-input').click()}
            >
              <Upload size={40} className="image-upload-icon" />
              <p className="image-upload-text">
                Click to upload images or drag and drop
              </p>
              <p className="image-upload-hint">
                Maximum 5 images, 5MB each (JPG, PNG)
              </p>
            </div>
            <input
              id="image-input"
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={handleImageUpload}
            />
            
            {imagePreviews.length > 0 && (
              <div className="image-previews">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="image-preview">
                    <img src={preview} alt={`Preview ${index + 1}`} />
                    <button
                      type="button"
                      className="image-preview-remove"
                      onClick={() => removeImage(index)}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button 
            type="submit" 
            className="submit-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="loading-spinner-small" />
                Submitting...
              </>
            ) : (
              <>
                <Send size={20} />
                Submit Issue
              </>
            )}
          </button>
        </form>
      </div>

      {/* Success Modal */}
      {successModal && (
        <div className="success-modal-overlay">
          <div className="success-modal">
            <div className="success-icon">
              <CheckCircle size={40} />
            </div>
            <h2>Issue Submitted Successfully!</h2>
            <p>Thank you for your feedback. We'll review your issue shortly.</p>
            <div className="tracking-number">{successModal.id}</div>
            <p style={{ fontSize: '0.9rem', color: '#666' }}>
              Save this tracking number for your records
            </p>
            <div className="success-modal-buttons">
              <button 
                className="btn-secondary"
                onClick={() => navigate('/issues')}
              >
                View All Issues
              </button>
              <button 
                className="btn-primary"
                onClick={() => navigate(`/issues/${successModal.id}`)}
              >
                View My Issue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

